/**
 * Corporate Price List Detail Page Component
 * /admin/corporate-price-lists/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import apiClient from '../api/client';

const CorporatePriceListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [priceList, setPriceList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [routePrices, setRoutePrices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [addRouteDialogOpen, setAddRouteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [routeFormData, setRouteFormData] = useState({
    pickup_location_id: '',
    dropoff_location_id: '',
    base_price: '',
    department: '',
    cost_center_code: '',
    priority: 'normal',
    min_distance: '',
    max_distance: '',
    notes: '',
    is_active: true,
  });

  useEffect(() => {
    loadPriceList();
    loadLocations();
  }, [id]);

  const loadPriceList = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getCorporatePriceList(id);
      setPriceList(data);
      setRoutePrices(data.route_prices || []);
    } catch (err) {
      setError(err.message || 'Failed to load price list details');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await apiClient.getLocations({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const handleAddRoutePrice = async () => {
    try {
      setSaving(true);
      setError('');
      const submitData = {
        price_list: id,
        pickup_location_id: routeFormData.pickup_location_id,
        dropoff_location_id: routeFormData.dropoff_location_id,
        base_price: parseFloat(routeFormData.base_price),
        is_active: routeFormData.is_active,
      };

      if (routeFormData.department) submitData.department = routeFormData.department;
      if (routeFormData.cost_center_code) submitData.cost_center_code = routeFormData.cost_center_code;
      if (routeFormData.priority) submitData.priority = routeFormData.priority;
      if (routeFormData.min_distance) submitData.min_distance = parseFloat(routeFormData.min_distance);
      if (routeFormData.max_distance) submitData.max_distance = parseFloat(routeFormData.max_distance);
      if (routeFormData.notes) submitData.notes = routeFormData.notes;

      await apiClient.createRoutePrice(submitData);
      setAddRouteDialogOpen(false);
      setRouteFormData({
        pickup_location_id: '',
        dropoff_location_id: '',
        base_price: '',
        department: '',
        cost_center_code: '',
        priority: 'normal',
        min_distance: '',
        max_distance: '',
        notes: '',
        is_active: true,
      });
      loadPriceList();
    } catch (err) {
      setError(err.message || 'Failed to add route price');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRoutePrice = async (routePriceId) => {
    if (!window.confirm('Are you sure you want to delete this route price?')) {
      return;
    }
    try {
      await apiClient.deleteRoutePrice(routePriceId);
      loadPriceList();
    } catch (err) {
      setError(err.message || 'Failed to delete route price');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !priceList) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/corporate-price-lists')}>
          Back to Price Lists
        </Button>
      </Container>
    );
  }

  if (!priceList) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6">Price list not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/corporate-price-lists')}>
          Back to Price Lists
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/corporate-price-lists')}
            sx={{ mb: 1 }}
          >
            Back to Price Lists
          </Button>
          <Typography variant="h4" component="h1">
            Price List Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Price List ID: {priceList.id.substring(0, 8)}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/admin/route-prices?price_list=${id}`)}
          >
            View All Route Prices
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/corporate-price-lists/${priceList.id}/edit`)}
          >
            Edit Price List
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Price List Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Price List Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Name:
                </Typography>
                <Typography variant="body1">{priceList.name}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Customer:
                </Typography>
                <Typography variant="body1">
                  {priceList.customer_name || priceList.customer_code || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Effective Date:
                </Typography>
                <Typography variant="body1">{formatDate(priceList.effective_date)}</Typography>
              </Box>
              {priceList.expiry_date && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Expiry Date:
                  </Typography>
                  <Typography variant="body1">{formatDate(priceList.expiry_date)}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={priceList.is_active ? 'Active' : 'Inactive'}
                  color={priceList.is_active ? 'success' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Route Prices Count */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Route Prices
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h3" color="primary">
              {routePrices.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total route prices in this list
            </Typography>
          </Paper>
        </Grid>

        {/* Route Prices Table */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Route Prices
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddRouteDialogOpen(true)}
              >
                Add Route Price
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Pickup Location</TableCell>
                    <TableCell>Dropoff Location</TableCell>
                    <TableCell>Base Price</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Cost Center</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routePrices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No route prices found. Click "Add Route Price" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    routePrices.map((routePrice) => (
                      <TableRow key={routePrice.id} hover>
                        <TableCell>
                          {routePrice.pickup_location?.name || '-'}
                        </TableCell>
                        <TableCell>
                          {routePrice.dropoff_location?.name || '-'}
                        </TableCell>
                        <TableCell>
                          {routePrice.base_price ? `AED ${parseFloat(routePrice.base_price).toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell>{routePrice.department || '-'}</TableCell>
                        <TableCell>{routePrice.cost_center_code || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={routePrice.priority || 'normal'}
                            size="small"
                            color={routePrice.priority === 'high' ? 'error' : routePrice.priority === 'medium' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={routePrice.is_active ? 'Active' : 'Inactive'}
                            color={routePrice.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/route-prices/${routePrice.id}`)}
                            title="View"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRoutePrice(routePrice.id)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Route Price Dialog */}
      <Dialog
        open={addRouteDialogOpen}
        onClose={() => setAddRouteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Route Price</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name || ''}
                value={locations.find(l => l.id === routeFormData.pickup_location_id) || null}
                onChange={(e, newValue) => {
                  setRouteFormData(prev => ({
                    ...prev,
                    pickup_location_id: newValue ? newValue.id : '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pickup Location"
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name || ''}
                value={locations.find(l => l.id === routeFormData.dropoff_location_id) || null}
                onChange={(e, newValue) => {
                  setRouteFormData(prev => ({
                    ...prev,
                    dropoff_location_id: newValue ? newValue.id : '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dropoff Location"
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Base Price"
                type="number"
                value={routeFormData.base_price}
                onChange={(e) => setRouteFormData(prev => ({ ...prev, base_price: e.target.value }))}
                required
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Department"
                value={routeFormData.department}
                onChange={(e) => setRouteFormData(prev => ({ ...prev, department: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Cost Center Code"
                value={routeFormData.cost_center_code}
                onChange={(e) => setRouteFormData(prev => ({ ...prev, cost_center_code: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={routeFormData.priority}
                  label="Priority"
                  onChange={(e) => setRouteFormData(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Min Distance (km)"
                type="number"
                value={routeFormData.min_distance}
                onChange={(e) => setRouteFormData(prev => ({ ...prev, min_distance: e.target.value }))}
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Max Distance (km)"
                type="number"
                value={routeFormData.max_distance}
                onChange={(e) => setRouteFormData(prev => ({ ...prev, max_distance: e.target.value }))}
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={routeFormData.notes}
                onChange={(e) => setRouteFormData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={routeFormData.is_active}
                  label="Status"
                  onChange={(e) => setRouteFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRouteDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleAddRoutePrice}
            variant="contained"
            disabled={saving || !routeFormData.pickup_location_id || !routeFormData.dropoff_location_id || !routeFormData.base_price}
          >
            {saving ? 'Adding...' : 'Add Route Price'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CorporatePriceListDetail;

